import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const sel = "mt-1 h-9 w-full rounded-md border border-stone-200 bg-white px-2 text-sm";

export function SessionEditDialog({ session, open, onOpenChange }) {
  const qc = useQueryClient();
  const [f, setF] = useState({});
  useEffect(() => {
    if (open) setF({ kind: session.is_seminar ? "seminario" : "lezione", course_id: session.course_id || "", track_type: session.track_type || "residenziale",
      date: session.date, start_time: session.start_time || "", title: session.title || "" });
  }, [open, session]);
  const { data: courses = [] } = useQuery({ queryKey: ["courses-all"], queryFn: async () => (await api.get("/courses")).data, enabled: open });
  const list = courses.filter((c) => !!c.is_seminar === (f.kind === "seminario") && (c.track_type || "residenziale") === f.track_type)
    .sort((a, b) => a.name.localeCompare(b.name));
  const mut = useMutation({
    mutationFn: async () => {
      const body = { track_type: f.track_type, date: f.date, start_time: f.start_time || null };
      if (f.course_id && f.course_id !== session.course_id) body.course_id = f.course_id;
      if (f.title.trim() && f.title !== session.title) body.title = f.title.trim();
      return (await api.patch(`/attendance/sessions/${session.id}`, body)).data;
    },
    onSuccess: () => {
      toast.success("Sessione aggiornata");
      qc.invalidateQueries({ queryKey: ["attendance-session", session.id] });
      qc.invalidateQueries({ queryKey: ["attendance-sessions"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v, ...(k === "kind" || k === "track_type" ? { course_id: "" } : {}) }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="session-edit-dialog">
        <DialogHeader><DialogTitle>Modifica sessione presenze</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Tipo</Label>
            <select value={f.kind} onChange={(e) => set("kind", e.target.value)} className={sel} data-testid="session-edit-kind">
              <option value="lezione">Lezione (corso)</option><option value="seminario">Seminario</option>
            </select></div>
          <div><Label className="text-xs">Percorso</Label>
            <select value={f.track_type} onChange={(e) => set("track_type", e.target.value)} className={sel} data-testid="session-edit-track">
              <option value="residenziale">Residenziale</option><option value="modulare">Modulare</option>
            </select></div>
          <div className="col-span-2"><Label className="text-xs">{f.kind === "seminario" ? "Seminario" : "Corso"}</Label>
            <select value={f.course_id} onChange={(e) => set("course_id", e.target.value)} className={sel} data-testid="session-edit-course">
              <option value="">— Scegli —</option>
              {list.map((c) => <option key={c.id} value={c.id}>{c.name}{c.code ? ` (${c.code})` : ""}</option>)}
            </select>
            {!list.length && <p className="text-[11px] text-amber-700 mt-1">Nessun {f.kind === "seminario" ? "seminario" : "corso"} per questo percorso.</p>}</div>
          <div><Label className="text-xs">Data</Label><Input type="date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="session-edit-date"/></div>
          <div><Label className="text-xs">Ora inizio</Label><Input type="time" value={f.start_time || ""} onChange={(e) => set("start_time", e.target.value)} className="mt-1" data-testid="session-edit-time"/></div>
          <div className="col-span-2"><Label className="text-xs">Titolo</Label><Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} className="mt-1" data-testid="session-edit-title"/></div>
        </div>
        <p className="text-[11px] text-stone-500">Le presenze già registrate restano invariate e passano al nuovo corso.</p>
        <Button onClick={() => mut.mutate()} disabled={!f.course_id || !f.date || mut.isPending} className="bg-[#1C352D] hover:bg-[#264A3E]" data-testid="session-edit-save">Salva modifiche</Button>
      </DialogContent>
    </Dialog>
  );
}
